<script setup lang="ts">
import { useField } from 'vee-validate';
import { omit } from 'lodash';
import TextField from './TextField.vue';
import type { VeeTextFieldProps } from '../types';
import { toRef, computed } from 'vue';
import { useVeeFormProps } from '../utils';

const props = withDefaults(defineProps<VeeTextFieldProps>(), { validateOnBlur: true });

const veeProps = useVeeFormProps(props);
// `validateOnBlur` only controls the validation here, it must not reach the TextField
const textFieldProps = computed(() => omit(veeProps.value, ['displayConverter', 'validateOnBlur']));

const { errorMessage, value, handleChange, handleBlur, meta } = useField<string | undefined>(
  toRef(() => props.name),
  undefined,
  {
    validateOnValueUpdate: false,
  },
);

function onUpdate(input: string | undefined) {
  // An empty API value is null. The text field reports that as "", which normalizes to undefined.
  if (input === value.value || (input == null && value.value == null)) return;
  handleChange(input, meta.touched);
}

function onBlur(event: FocusEvent) {
  handleBlur(event, props.validateOnBlur);
}

const modelValue = computed(() => {
  if (!value.value) return value.value;
  if (props.displayConverter) return props.displayConverter(value.value);
  if (
    props.outputConverter &&
    (veeProps.value.mode === 'details' || veeProps.value.disabled === true)
  ) {
    return props.outputConverter(value.value);
  }
  return value.value;
});

const slots = defineSlots<{ [key: string]: unknown }>();
</script>

<template>
  <TextField
    v-bind="textFieldProps"
    :modelValue="modelValue"
    :errorMessage="errorMessage"
    @update:modelValue="onUpdate"
    @blur="onBlur"
  >
    <template v-for="(_, name) in slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData" />
    </template>
  </TextField>
</template>
